/* ==========================================================================
   Blog article sidebars
   - gives the article headings the ids the server-rendered TOC links to
   - highlights the heading currently being read
   - mobile collapse toggle for the TOC
   - GA4 / dataLayer tracking for popular product clicks
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 1280px)';

  function parseAnchors(toc) {
    try {
      return JSON.parse(toc.getAttribute('data-toc-anchors') || '[]');
    } catch (err) {
      return [];
    }
  }

  function initToc() {
    var toc = document.querySelector('[data-article-toc]');
    if (!toc) return;

    var content = document.querySelector('.article__content');
    if (!content) return;

    var selector = toc.getAttribute('data-toc-selector') || 'h2';
    var headings = Array.prototype.slice.call(
      content.querySelectorAll(
        selector
          .split(',')
          .map(function (part) {
            return '.article__content ' + part.trim();
          })
          .join(', ')
      )
    );
    if (!headings.length) return;

    // The TOC was rendered from the same markup in the same order, so the
    // anchors line up by index. Existing ids always win.
    var anchors = parseAnchors(toc);
    headings.forEach(function (heading, index) {
      if (!heading.id && anchors[index]) {
        heading.id = anchors[index];
      }
    });

    var links = Array.prototype.slice.call(toc.querySelectorAll('[data-toc-link]'));
    var linkByHash = {};
    links.forEach(function (link) {
      linkByHash[link.getAttribute('href')] = link;
    });

    // On desktop the rail itself scrolls, so keep the active link in view.
    var scroller = toc.closest('.article__sidebar') || toc;

    function setCurrent(heading) {
      var target = heading ? linkByHash['#' + heading.id] : null;
      links.forEach(function (link) {
        if (link === target) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (!target || scroller.scrollHeight <= scroller.clientHeight + 4) return;

      var linkBox = target.getBoundingClientRect();
      var railBox = scroller.getBoundingClientRect();
      var delta = linkBox.top - railBox.top - railBox.height / 2 + linkBox.height / 2;
      if (Math.abs(delta) > 24) {
        scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
      }
    }

    // Scroll spy: the active heading is the last one whose top edge has
    // passed the reading line near the top of the viewport.
    var ticking = false;
    function updateActive() {
      ticking = false;
      var line = window.innerHeight * 0.25;
      var active = null;
      for (var i = 0; i < headings.length; i += 1) {
        if (headings[i].getBoundingClientRect().top <= line) {
          active = headings[i];
        } else {
          break;
        }
      }
      if (!active && headings.length) {
        var first = headings[0].getBoundingClientRect();
        if (first.top < window.innerHeight) active = headings[0];
      }
      if (active !== updateActive.last) {
        updateActive.last = active;
        setCurrent(active);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActive);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateActive();

    // Smooth scroll with room for the sticky header.
    toc.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('[data-toc-link]') : null;
      if (!link) return;

      var hash = link.getAttribute('href');
      var target = hash && hash.length > 1 ? document.getElementById(hash.slice(1)) : null;
      if (!target) return;

      event.preventDefault();

      var styles = getComputedStyle(document.documentElement);
      var headerHeight = parseFloat(styles.getPropertyValue('--gsc-header-height')) || 80;
      var top = window.pageYOffset + target.getBoundingClientRect().top - headerHeight - 24;

      window.scrollTo({
        top: top > 0 ? top : 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });

      if (history.replaceState) history.replaceState(null, '', hash);

      if (!window.matchMedia(DESKTOP_QUERY).matches) setOpen(false);
    });

    // Mobile collapse.
    var toggle = toc.querySelector('[data-toc-toggle]');

    function setOpen(open) {
      toc.setAttribute('data-open', open ? 'true' : 'false');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (toggle) {
      toc.querySelector('.article-toc__head').addEventListener('click', function (event) {
        if (window.matchMedia(DESKTOP_QUERY).matches) return;
        if (event.target.closest('[data-toc-link]')) return;
        setOpen(toc.getAttribute('data-open') !== 'true');
      });
    }

    // If the page was opened on a heading anchor, reveal the list.
    if (window.location.hash && linkByHash[window.location.hash]) setOpen(true);
  }

  function initPopularTracking() {
    var rail = document.querySelector('[data-article-popular]');
    if (!rail) return;

    rail.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('[data-popular-click]') : null;
      if (!link) return;

      var card = link.closest('.article-popular__item');
      if (!card) return;

      var price = parseFloat(card.getAttribute('data-track-price') || '0');
      var item = {
        item_id: card.getAttribute('data-track-variant-id') || card.getAttribute('data-track-product-id'),
        item_name: card.getAttribute('data-track-title'),
        price: isNaN(price) ? 0 : price,
        item_brand: card.getAttribute('data-track-brand'),
        item_category: card.getAttribute('data-track-category'),
        index: parseInt(card.getAttribute('data-track-position') || '0', 10),
        quantity: 1
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_name: 'Blog Sidebar Popular Products',
          item_list_id: 'blog_sidebar_popular',
          items: [item]
        },
        blog_article_title: card.getAttribute('data-track-article-title')
      });

      document.dispatchEvent(
        new CustomEvent('blog_sidebar_popular:click', {
          detail: { event: 'select_item', item: item }
        })
      );
    });
  }

  // The sticky offset has to clear the theme's fixed header. Its height is not
  // a constant (announcement bar, logo size, editor previews), so measure it
  // instead of trusting --gsc-header-height, which covers the bar only.
  function syncStickyOffset() {
    var layout = document.querySelector('.article__layout');
    if (!layout) return;

    var header = document.querySelector('.shopify-section-header') || document.querySelector('header');
    if (!header) return;

    var position = getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return;

    var bottom = header.getBoundingClientRect().bottom;
    if (!(bottom > 0) || bottom > 240) return; // nonsense measurement, keep the CSS default

    layout.style.setProperty('--sn-sidebar-sticky-top', Math.round(bottom + 24) + 'px');
  }

  function init() {
    syncStickyOffset();
    window.addEventListener('resize', syncStickyOffset, { passive: true });
    initToc();
    initPopularTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
